import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBasePath } from '../config/nav-helpers';
import { useTheme } from '../config/theme';

export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const basePath = useBasePath();

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Don't trigger when typing in inputs
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if ((e.target as HTMLElement).isContentEditable) return;

      // G + key for "go to" navigation (dashboard + sales)
      if (e.key === 'g' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const handler = (next: KeyboardEvent) => {
          next.preventDefault();
          const routes: Record<string, string> = {
            h: '', // home / command center
            r: '/revenue',
            c: '/clients',
            p: '/pipeline',
            i: '/intelligence',
            a: '/actions',
            s: '/settings',
            m: '/meetings',
            k: '/marketing',
            // Sales shortcuts — same order as the Sales nav
            '1': '/sales',
            '2': '/sales/pipeline',
            '3': '/sales/coaching',
            '4': '/sales/meetings',
            '5': '/sales/settings',
          };
          const target = routes[next.key];
          if (target != null) {
            // Sales routes are absolute, hub routes are relative to basePath
            if (target.startsWith('/sales')) {
              navigate(target);
            } else {
              navigate(`${basePath}${target}`);
            }
          }
          document.removeEventListener('keydown', handler);
        };
        document.addEventListener('keydown', handler, { once: true });
        setTimeout(() => document.removeEventListener('keydown', handler), 1000);
        return;
      }

      // Quick actions
      if (e.key === '?' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        // Show shortcuts help — dispatch custom event
        window.dispatchEvent(new CustomEvent('waypoint:show-shortcuts'));
        return;
      }

      if (e.key === 'a' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        // handled by the "g" prefix — let it pass
      }

      if (e.key === '/' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('waypoint:open-chat'));
        return;
      }

      if (e.key === 't' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setTheme(theme === 'dark' ? 'light' : 'dark');
        return;
      }

      // Ctrl+Shift+V — toggle voice input
      if (e.key === 'V' && (e.metaKey || e.ctrlKey) && e.shiftKey) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('waypoint:toggle-voice'));
        return;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, basePath, theme, setTheme]);
}

export const SHORTCUT_MAP = [
  { keys: ['g', 'h'], label: 'Go to Command Center' },
  { keys: ['g', 'r'], label: 'Go to Finance' },
  { keys: ['g', 'c'], label: 'Go to Clients' },
  { keys: ['g', 'p'], label: 'Go to Pipeline' },
  { keys: ['g', 'i'], label: 'Go to Intelligence' },
  { keys: ['g', 'm'], label: 'Go to Meetings' },
  { keys: ['g', 'k'], label: 'Go to Marketing' },
  { keys: ['g', 'a'], label: 'Go to Actions' },
  { keys: ['g', 's'], label: 'Go to Settings' },
  { keys: ['g', '1'], label: 'Go to Sales Home' },
  { keys: ['g', '2'], label: 'Go to Sales Pipeline' },
  { keys: ['g', '3'], label: 'Go to Sales Deals' },
  { keys: ['g', '4'], label: 'Go to Sales Coaching' },
  { keys: ['g', '5'], label: 'Go to Sales Leaderboard' },
  { keys: ['⌘', 'K'], label: 'Open Command Palette' },
  { keys: ['⌘', '⇧', 'K'], label: 'Semantic Search' },
  { keys: ['/'], label: 'Open AI Assistant' },
  { keys: ['t'], label: 'Toggle Dark Mode' },
  { keys: ['?'], label: 'Show Keyboard Shortcuts' },
  { keys: ['⌘', '⇧', 'V'], label: 'Toggle Voice Input' },
] as const;
